import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { hostRole } from '../config/domains';
import { isMerchantBrandedPath, isPublicMarketingPath, seoForPath } from '../config/seo';
import { applySeo } from '../hooks/useSeo';

// ---------------------------------------------------------------------------
// SeoManager — gives every public route its own head, on every navigation.
//
// Mounted once, above the route table. There is no per-page <Seo> component to
// remember to add, because the failure mode of "remember to add it" is a page
// that silently inherits the previous page's title — which is exactly the
// defect this chunk exists to fix, reappearing one level up.
//
// MOST OF THIS FILE IS ABOUT WHEN TO STAY SILENT, and the reason is that one
// React bundle serves three different identities:
//
//   · ReCurr's marketing pages, which this component owns outright;
//   · ReCurr's platform pages, where it only resets the head so that walking
//     from /legal/tos into the dashboard cannot leave the Terms' title and
//     canonical URL behind on a page that is not the Terms;
//   · a MERCHANT's storefront, checkout and payment links, which wear the
//     merchant's brand and must never be stamped with ReCurr's.
//
// The third is the one that would break something. React runs a child's effects
// before its parent's, so a SeoManager that spoke for every path would overwrite
// PublicPageView's merchant title a moment after the page set it, and put
// "ReCurr — Subscription billing for South Africa" in the tab of a customer
// half-way through paying somebody else. Two independent guards stop that: the
// merchant-branded path prefixes, and — on a merchant's own hostname — anything
// that is not one of the shared public pages.
// ---------------------------------------------------------------------------

const SeoManager: React.FC = () => {
  const { pathname, search } = useLocation();

  // /legal/:docType?merchant=<id> renders the MERCHANT's version of that
  // policy, not ReCurr's. Same URL, different document, so the head this file
  // would write for it would name the wrong company.
  const merchantScoped = new URLSearchParams(search).has('merchant');

  const speaks =
    !merchantScoped &&
    !isMerchantBrandedPath(pathname) &&
    (isPublicMarketingPath(pathname) || hostRole() !== 'unified');

  useEffect(() => {
    if (!speaks) return;
    applySeo(seoForPath(pathname));
  }, [pathname, speaks]);

  return null;
};

export default SeoManager;
